import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Crosshair, RefreshCw, TrendingUp, Users, BarChart3,
  Globe, Mail, Phone, Share2, FileText, Megaphone, Search,
  ArrowUpRight, ArrowDownRight, Inbox
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/shared/ExportButton';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_ICONS = {
  'Website': Globe,
  'Referral': Users,
  'Email': Mail,
  'Phone': Phone,
  'Social Media': Share2,
  'Form': FileText,
  'Ads': Megaphone,
  'Direct': Search,
};

const SOURCE_COLORS = {
  'Website': 'bg-blue-100 text-blue-700',
  'Referral': 'bg-purple-100 text-purple-700',
  'Email': 'bg-amber-100 text-amber-700',
  'Phone': 'bg-green-100 text-green-700',
  'Social Media': 'bg-pink-100 text-pink-700',
  'Form': 'bg-teal-100 text-teal-700',
  'Ads': 'bg-orange-100 text-orange-700',
  'Direct': 'bg-gray-100 text-gray-700',
};

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function LeadSourceTrackerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryCount, setInquiryCount] = useState({ new: 0, total: 0 });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [srcRes, inqRes] = await Promise.all([
        axios.get(`${API}/api/lead-sources/dashboard`),
        axios.get(`${API}/api/lead-sources/inquiry-count`).catch(() => ({ data: { new: 0, total: 0 } })),
      ]);
      setData(srcRes.data);
      setInquiryCount(inqRes.data);
    } catch { toast.error('Failed to load lead sources'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  const totalLeads = data?.total_leads || 0;
  const bySrc = data?.by_source || [];
  const convData = data?.conversion_by_source || [];
  const utmData = data?.utm_data || [];
  const topSource = bySrc.length > 0 ? bySrc[0] : null;
  const avgConv = convData.length > 0 ? (convData.reduce((s, c) => s + c.rate, 0) / convData.length).toFixed(1) : 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="lead-source-tracker-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Lead Source Tracker<PageInfoTooltip pageKey="lead-source-tracker" /></h1>
          <p className="text-gray-medium text-sm mt-1">Understand where your leads come from and which sources convert best</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={(data?.by_source || []).map(s => ({ source: s.source, count: s.count, conversion_rate: s.conversion_rate }))}
            filename="lead_sources"
            columns={['source', 'count', 'conversion_rate']}
          />
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Leads" value={totalLeads} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Crosshair} label="Sources" value={bySrc.length} color="bg-purple-50 text-purple-600" />
        <StatCard icon={TrendingUp} label="Avg Conversion" value={avgConv} color="bg-emerald-50 text-emerald-600" suffix="%" />
        <StatCard icon={BarChart3} label="Top Source" value={topSource?.source || 'N/A'} color="bg-amber-50 text-amber-600" />
      </div>

      {/* Product Inquiry Lead Source */}
      {inquiryCount.total > 0 && (
        <Card
          className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/sales/inquiries')}
          data-testid="inquiry-lead-source-card"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Inbox className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Product Showcase Inquiries</p>
                <p className="text-xs text-gray-500">{inquiryCount.total} total inquiries · {inquiryCount.new} new</p>
              </div>
            </div>
            {inquiryCount.new > 0 && (
              <Badge className="bg-blue-500 text-white text-xs">{inquiryCount.new} new</Badge>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sources Breakdown */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Lead Sources</CardTitle></CardHeader>
          <CardContent>
            {bySrc.length === 0 ? (
              <div className="text-center py-8 text-gray-medium text-sm">
                <Crosshair className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>No lead source data available</p>
                <p className="text-xs mt-1">Import contacts to see source analytics</p>
              </div>
            ) : (
              <div className="space-y-2">
                {bySrc.map(src => {
                  const pct = totalLeads > 0 ? ((src.count / totalLeads) * 100).toFixed(1) : 0;
                  const SrcIcon = SOURCE_ICONS[src.source] || Globe;
                  const clr = SOURCE_COLORS[src.source] || 'bg-gray-100 text-gray-700';
                  return (
                    <div key={src.source} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`source-${src.source}`}>
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", clr)}>
                        <SrcIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-text-primary">{src.source}</span>
                          <span className="font-semibold text-sm">{src.count}</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <span className="text-xs text-gray-medium flex-shrink-0">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion by Source */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Conversion by Source</CardTitle></CardHeader>
          <CardContent>
            {convData.length === 0 ? (
              <div className="text-center py-8 text-gray-medium text-sm">No conversion data available</div>
            ) : (
              <div className="space-y-2">
                {convData.map(c => (
                  <div key={c.source} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100" data-testid={`conv-${c.source}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm text-text-primary">{c.source}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-medium">{c.total} leads</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-emerald-600">{c.converted} converted</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {c.rate > 0 ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" /> : <ArrowDownRight className="h-3.5 w-3.5 text-gray-400" />}
                      <span className={cn("text-sm font-semibold", c.rate > 0 ? "text-emerald-600" : "text-gray-400")}>{c.rate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* UTM Data */}
      {utmData.length > 0 && (
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">UTM Campaign Data</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left p-2 text-gray-medium font-medium">Source</th>
                  <th className="text-left p-2 text-gray-medium font-medium">Medium</th>
                  <th className="text-left p-2 text-gray-medium font-medium">Campaign</th>
                  <th className="text-right p-2 text-gray-medium font-medium">Leads</th>
                </tr></thead>
                <tbody>
                  {utmData.map((u, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-2 font-medium text-text-primary">{u.utm_source || '—'}</td>
                      <td className="p-2 text-gray-medium">{u.utm_medium || '—'}</td>
                      <td className="p-2 text-gray-medium">{u.utm_campaign || '—'}</td>
                      <td className="p-2 text-right font-semibold">{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
